export interface CodeTemplate {
  template: string
  language: string
  syntax: string
}

const codeTemplates: Record<string, CodeTemplate> = {
  curl: {
    template: '{{curl}}',
    language: 'curl',
    syntax: 'bash',
  },
  python: {
    template: `# This example shows how to send multiple images to the model and get predictions
import requests
from pathlib import Path
import json

# Authentication headers - DO NOT SHARE these with anyone!
headers = {
{{headers}}
}

def get_predictions(image_paths):
    """
    Send multiple images to the model and get predictions
    Args:
        image_paths: List of paths to image files
    Returns:
        List of predictions for each image
    """
    try:
        # Prepare multiple files
        files = []
        for image_path in image_paths:
            files.append(('files', open(image_path, 'rb')))
        
        # Send the request with multiple files
        response = requests.post('{{url}}', headers=headers, files=files)
        
        # Check if the request was successful
        response.raise_for_status()
        
        # Try to parse the JSON response
        try:
            predictions = response.json()
            return predictions
        except json.JSONDecodeError as e:
            print(f"Error decoding JSON response: {str(e)}")
            print(f"Raw response: {response.text}")
            return None
    
    except requests.exceptions.RequestException as e:
        print(f"Request error: {str(e)}")
        if hasattr(e, 'response') and e.response is not None:
            print(f"Response status code: {e.response.status_code}")
            print(f"Response text: {e.response.text}")
        return None
    except Exception as e:
        print(f"An error occurred: {str(e)}")
        return None
    finally:
        # Close all opened files
        for _, file in files:
            try:
                file.close()
            except:
                pass

# Example usage:
if __name__ == "__main__":
    # List of image paths to process
    image_paths = [
        'path/to/image1.jpg',
        'path/to/image2.jpg',
        'path/to/image3.jpg'
    ]
    
    # Get predictions for all images
    results = get_predictions(image_paths)
    
    # Process the results
    if results:
        for image_path, prediction in zip(image_paths, results):
            print(f"\\nPredictions for {Path(image_path).name}:")
            print(json.dumps(prediction, indent=2))  # Pretty print the JSON
    else:
        print("\\nFailed to get predictions. Please check the error messages above.")`,
    language: 'Python',
    syntax: 'python',
  },
  javascript: {
    template: `// This example shows how to send multiple images to the model and get predictions

// Authentication headers - DO NOT SHARE these with anyone!
const headers = {
{{headers}}
};

/**
 * Send multiple images to the model and get predictions
 * @param {File[]} imageFiles - Array of image files to process
 * @returns {Promise<Array>} Array of predictions for each image
 */
async function getPredictions(imageFiles) {
    try {
        // Prepare form data with multiple files
        const formData = new FormData();
        imageFiles.forEach(file => {
            formData.append('files', file);
        });

        // Send the request
        const response = await fetch('{{url}}', {
            method: 'POST',
            headers: headers,
            body: formData
        });

        // Get and return the predictions
        const predictions = await response.json();
        return predictions;
    } catch (error) {
        console.error('Error:', error);
        return null;
    }
}

// Example usage with file input:
// 1. In your HTML:
// <input type="file" id="imageInput" accept="image/*" multiple>
// <div id="results"></div>

// 2. Handle the file selection:
document.getElementById('imageInput').onchange = async function(event) {
    const imageFiles = Array.from(event.target.files);
    const resultsDiv = document.getElementById('results');
    
    // Show loading state
    resultsDiv.innerHTML = 'Processing images...';
    
    // Get predictions for all images
    const predictions = await getPredictions(imageFiles);
    
    // Display results
    if (predictions) {
        resultsDiv.innerHTML = imageFiles.map((file, index) => \`
            <div>
                <h4>\${file.name}</h4>
                <pre>\${JSON.stringify(predictions[index], null, 2)}</pre>
            </div>
        \`).join('');
    } else {
        resultsDiv.innerHTML = 'Error processing images';
    }
};`,
    language: 'JavaScript',
    syntax: 'javascript',
  },
  typescript: {
    template: `// This example shows how to send multiple images to the model and get predictions

// Authentication headers - DO NOT SHARE these with anyone!
const headers = {
{{headers}}
};

/**
 * Send multiple images to the model and get predictions
 * @param imageFiles - Array of image files to process
 * @returns Array of predictions for each image
 */
async function getPredictions(imageFiles: File[]): Promise<any[] | null> {
    try {
        // Prepare form data with multiple files
        const formData = new FormData();
        imageFiles.forEach(file => {
            formData.append('files', file);
        });

        // Send the request
        const response = await fetch('{{url}}', {
            method: 'POST',
            headers: headers,
            body: formData
        });

        // Get and return the predictions
        const predictions = await response.json();
        return predictions;
    } catch (error) {
        console.error('Error:', error);
        return null;
    }
}

// Example usage with file input:
// 1. In your HTML:
// <input type="file" id="imageInput" accept="image/*" multiple>
// <div id="results"></div>

// 2. Handle the file selection:
const imageInput = document.getElementById('imageInput') as HTMLInputElement;
const resultsDiv = document.getElementById('results') as HTMLDivElement;

imageInput.onchange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target.files) {
        const imageFiles = Array.from(target.files);
        
        // Show loading state
        resultsDiv.innerHTML = 'Processing images...';
        
        // Get predictions for all images
        const predictions = await getPredictions(imageFiles);
        
        // Display results
        if (predictions) {
            resultsDiv.innerHTML = imageFiles.map((file, index) => \`
                <div>
                    <h4>\${file.name}</h4>
                    <pre>\${JSON.stringify(predictions[index], null, 2)}</pre>
                </div>
            \`).join('');
        } else {
            resultsDiv.innerHTML = 'Error processing images';
        }
    }
};`,
    language: 'TypeScript',
    syntax: 'typescript',
  },
  go: {
    template: `// This example shows how to send multiple images to the model and get predictions
package main

import (
    "bytes"
    "encoding/json"
    "fmt"
    "io"
    "mime/multipart"
    "net/http"
    "os"
    "path/filepath"
)

// sendImages sends multiple images to the model and returns predictions
func sendImages(imagePaths []string) error {
    // Create a new form file
    body := &bytes.Buffer{}
    writer := multipart.NewWriter(body)

    // Add all images to the form
    for _, imagePath := range imagePaths {
        file, err := os.Open(imagePath)
        if err != nil {
            return fmt.Errorf("error opening file %s: %v", imagePath, err)
        }
        defer file.Close()

        part, err := writer.CreateFormFile("files", filepath.Base(imagePath))
        if err != nil {
            return fmt.Errorf("error creating form file: %v", err)
        }
        
        _, err = io.Copy(part, file)
        if err != nil {
            return fmt.Errorf("error copying file: %v", err)
        }
    }
    writer.Close()

    // Create the request
    request, err := http.NewRequest("POST", "{{url}}", body)
    if err != nil {
        return fmt.Errorf("error creating request: %v", err)
    }

    // Add authentication headers - DO NOT SHARE these with anyone!
{{headers}}
    // Set the content type
    request.Header.Set("Content-Type", writer.FormDataContentType())

    // Send the request
    client := &http.Client{}
    response, err := client.Do(request)
    if err != nil {
        return fmt.Errorf("error sending request: %v", err)
    }
    defer response.Body.Close()

    // Read and process the response
    var predictions []map[string]interface{}
    if err := json.NewDecoder(response.Body).Decode(&predictions); err != nil {
        return fmt.Errorf("error decoding response: %v", err)
    }

    // Print predictions for each image
    for i, prediction := range predictions {
        fmt.Printf("Predictions for %s:\\n", filepath.Base(imagePaths[i]))
        prettyJSON, _ := json.MarshalIndent(prediction, "", "    ")
        fmt.Println(string(prettyJSON))
    }

    return nil
}

func main() {
    // List of images to process
    imagePaths := []string{
        "path/to/image1.jpg",
        "path/to/image2.jpg",
        "path/to/image3.jpg",
    }

    if err := sendImages(imagePaths); err != nil {
        fmt.Printf("Error: %v\\n", err)
    }
}`,
    language: 'Go',
    syntax: 'go',
  },
}

function parseCurl(curlCommand: string): {
  url: string
  headers: Record<string, string>
  isMultipart: boolean
  formData: Record<string, string>
} {
  // Extract the full URL including the protocol and domain
  const urlMatch = curlCommand.match(/POST ['"]((https?:\/\/)?[^'"]*)['"]/)
  let url = urlMatch ? urlMatch[1] : ''

  // Replace development domain with production domain
  url = url.replace('dev.emeraldai-dev.com', 'go-prod.agileview-ai.com')

  // Ensure URL starts with https:// or http://
  const fullUrl = url.startsWith('http') ? url : `https://${url}`

  const headers: Record<string, string> = {}
  // Updated regex to handle more header formats
  const headerPattern =
    /--header ['"]([^:]+):\s*([^'"]+)['"]|--header "([^:]+):\s*([^"]+)"|--header ['"]([^:]+):\s*([^'"]+)['"]|--header "([^:]+):\s*([^"]+)"/g
  let match
  while ((match = headerPattern.exec(curlCommand)) !== null) {
    // match groups: [full match, single quote key, value, double quote key, value, single quote key, value, double quote key, value]
    const key = match[1] || match[3] || match[5] || match[7]
    const value = match[2] || match[4] || match[6] || match[8]
    // Skip Content-Type header
    if (key && value && key !== 'Content-Type') {
      headers[key] = value
    }
  }

  const formData: Record<string, string> = {}
  const formMatches = curlCommand.matchAll(/--form ['"]([^=]+)=([^'"]+)['"]/g)
  for (const match of formMatches) {
    formData[match[1]] = match[2]
  }

  const isMultipart = headers['Content-Type']?.includes('multipart/form-data') || false

  return { url: fullUrl, headers, isMultipart, formData }
}

export function translateCurl(curlCommand: string, targetLanguage: string): string {
  // For curl, just return the original command
  if (targetLanguage === 'curl') {
    return curlCommand
  }

  const template = codeTemplates[targetLanguage]
  if (!template) return curlCommand

  const { url, headers, isMultipart, formData } = parseCurl(curlCommand)
  const filePath = formData['files']?.replace('@', '') || '/path/to/file'

  // Only remove Content-Type header for multipart requests, keep all other headers
  const filteredHeaders = { ...headers }
  if (isMultipart) {
    delete filteredHeaders['Content-Type']
  }

  let code = template.template.replace('{{url}}', url).replace('{{file_path}}', filePath)

  // Format headers with proper indentation and quotes
  const headersStr = Object.entries(filteredHeaders)
    .map(([key, value]) => `    '${key}': '${value}'`)
    .join(',\n')

  code = code.replace('{{headers}}', headersStr)

  return code
}

export const supportedLanguages = Object.entries(codeTemplates).map(([key, value]) => ({
  value: key,
  label: value.language,
  syntax: value.syntax,
}))
